
import { mapState, mapActions } from "vuex";
import { reduce, minBy } from "ramda";

import {
  saleTagMixin,
  gtmEventMixin,
  metaColourMixin,
  tagableMixin,
} from "~/mixins";
import { slugify, camelize } from "~/utils";

export default {
  mixins: [saleTagMixin, metaColourMixin, gtmEventMixin, tagableMixin],

  inject: { uid: { default: "" } },

  props: {
    from: { type: String, default: "" },
    product: { type: Object, required: true },
    selectedVariant: { type: Object, required: true },
    isRefined: { type: Boolean, default: false },
    position: { type: [Number, String], required: true },
    disablePlpPricing: { type: Boolean, default: false },
  },

  computed: {
    ...mapState(["siteConfig"]),
    ...mapState("productsSearch", ["facetParams"]),

    metafields() {
      const variant = this.plpTileSelectedVariant || this.selectedVariant;

      return {
        ...variant?.metafields,
        ...this.product?.metafields,
      };
    },

    minPriceVariant() {
      return !this.isRefined
        ? reduce(
            minBy(v => Number(v.price)),
            { price: Infinity },
            this.product.variants
          )
        : null;
    },

    metaColors() {
      const metaColorArgs = this.product.variants;
      return this.getMetaColors(metaColorArgs);
    },

    tags() {
      return this.product?.tags ?? [];
    },

    listname() {
      return this.source ? `${this.source} - ${this.uid}` : this.uid;
    },

    productPriceProps() {
      const variants = Array.isArray(this.product?.variants)
        ? this.product.variants
        : [];

      const safeFallback = this.selectedVariant || variants[0] || {};

      if (this.disablePlpPricing) {
        return {
          product: this.product,
          selectedVariant: safeFallback,
          minPriceVariant: safeFallback,
          maxPriceVariant: safeFallback,
          filteredSizesCount: 1,
          sizeFilterType: "single",
        };
      }

      const sizesArray = this.plpSelectedSizes;
      const colorsArray = this.plpSelectedColors;

      const sizeFilterType =
        sizesArray.length === 0
          ? "none"
          : sizesArray.length === 1
          ? "single"
          : "multiple";

      const getGlobalMin = () => {
        if (!variants.length) return safeFallback;

        return variants.reduce(
          (p, c) => (Number(c.price) < Number(p.price) ? c : p),
          variants[0]
        );
      };

      const globalMin = getGlobalMin();

      /* ----------------------------------------
       * Start with all variants
       * ---------------------------------------- */
      let filteredVariants = [...variants];

      /* ----------------------------------------
       * Apply Size Filter
       * ---------------------------------------- */
      if (sizesArray.length) {
        filteredVariants = filteredVariants.filter(v => {
          const size = this.extractSize(v);
          return size && sizesArray.includes(size);
        });
      }

      /* ----------------------------------------
       * Apply Color Filter
       * ---------------------------------------- */
      if (colorsArray.length) {
        filteredVariants = filteredVariants.filter(v => {
          const color = this.extractColor(v);

          return color && colorsArray.includes(color);
        });
      }

      if (!filteredVariants.length) {
        /* ----------------------------------------
         * Size + Color fallback
         * Stay within selected size family
         * ---------------------------------------- */
        if (sizesArray.length && colorsArray.length) {
          const sizeVariants = variants.filter(v => {
            const size = this.extractSize(v);
            return size && sizesArray.includes(size);
          });

          if (sizeVariants.length) {
            const cheapestSizeVariant = sizeVariants.reduce(
              (p, c) => (Number(c.price) < Number(p.price) ? c : p),
              sizeVariants[0]
            );

            return {
              product: this.product,
              selectedVariant: cheapestSizeVariant,
              minPriceVariant: cheapestSizeVariant,
              maxPriceVariant: cheapestSizeVariant,
              sizeFilterType,
              filteredSizesCount: sizesArray.length,
            };
          }
        }

        /* ----------------------------------------
         * Global fallback
         * ---------------------------------------- */
        return {
          product: this.product,
          selectedVariant: globalMin,
          minPriceVariant: globalMin,
          maxPriceVariant: globalMin,
          sizeFilterType,
          filteredSizesCount: sizesArray.length,
        };
      }

      /* ----------------------------------------
       * Lowest priced matching variant
       * ---------------------------------------- */
      const minVariant = filteredVariants.reduce(
        (p, c) => (Number(c.price) < Number(p.price) ? c : p),
        filteredVariants[0]
      );

      return {
        product: this.product,
        selectedVariant: minVariant,
        minPriceVariant: minVariant,
        maxPriceVariant: minVariant,
        sizeFilterType,
        filteredSizesCount: sizesArray.length,
      };
    },

    productImagesProps() {
      return {
        mainImage:
          this.getVariantImage(this.plpTileSelectedVariant) ??
          this.product.featuredImage ??
          {},
        selectedVariant: this.plpTileSelectedVariant,
        product: this.product,
        metafields: this.metafields,
        position: this.position,
        listname: this.listname,
      };
    },

    productColorProps() {
      return {
        colorSwatches: this.product.color_swatches,
        handle: this.product.handle,
      };
    },

    ratingProps() {
      return {
        handle: this.product.handle,
        reviewsAverage: this.metafields.reviews_average,
        reviewsCount: this.metafields.reviews_count,
        selectedVariantId: this.selectedVariant.id,
      };
    },

    title() {
      const feel = this.selectedOptions?.["comfort-feel"] ?? "";
      return this.oneProductMultipleFeels
        ? `${this.product.title} - ${feel}`
        : `${this.product.title}`;
    },

    selectedOptions() {
      const variant = this.plpTileSelectedVariant || this.selectedVariant;

      const selectedOptions = variant?.selectedOptions ?? {};

      return Object.entries(selectedOptions).reduce(
        (acc, [k, v]) => ({
          ...acc,
          [slugify(camelize(k, true, " "))]: v.value ?? v,
        }),
        {}
      );
    },

    imagesPresent() {
      return (
        Array.isArray(this.plpTileSelectedVariant?.images) &&
        this.plpTileSelectedVariant.images.length > 0
      );
    },

    isPlpTile() {
      return !this.disablePlpPricing;
    },

    plpTileSelectedVariant() {
      // Non-PLP : preserve existing behaviour
      if (!this.isPlpTile) {
        return this.selectedVariant;
      }

      const variants = Array.isArray(this.product?.variants)
        ? this.product.variants
        : [];

      if (!variants.length) {
        return this.selectedVariant;
      }

      const selectedSizes = this.plpSelectedSizes;
      const selectedColors = this.plpSelectedColors;

      /* ----------------------------------------
       * SIZE FILTER EXISTS
       * ---------------------------------------- */
      if (selectedSizes.length) {
        const pricingVariant =
          this.productPriceProps?.selectedVariant || this.selectedVariant;

        const pricingSize = this.extractSize(pricingVariant);

        // Restrict ALL image logic to same size family
        const sameSizeVariants = variants.filter(v => {
          return this.extractSize(v) === pricingSize;
        });

        if (!sameSizeVariants.length) {
          return pricingVariant;
        }

        /* ----------------------------------------
         * Try exact size + color match
         * ---------------------------------------- */
        if (selectedColors.length) {
          const exactMatch = sameSizeVariants.find(v => {
            const color = this.extractColor(v);
            const hasImage = this.getVariantImage(v);

            return color && selectedColors.includes(color) && hasImage;
          });

          if (exactMatch) {
            return exactMatch;
          }
        }

        /* ----------------------------------------
         * Pricing variant image
         * ---------------------------------------- */
        const pricingHasImage = this.getVariantImage(pricingVariant);

        if (pricingHasImage) {
          return pricingVariant;
        }

        /* ----------------------------------------
         * Any same-size variant with image
         * ---------------------------------------- */
        const sameSizeWithImage = sameSizeVariants.find(v => {
          return this.getVariantImage(v);
        });

        if (sameSizeWithImage) {
          return sameSizeWithImage;
        }

        return pricingVariant;
      }

      /* ----------------------------------------
       * COLOR ONLY
       * ---------------------------------------- */
      if (selectedColors.length) {
        return this.productPriceProps?.selectedVariant || this.selectedVariant;
      }

      /* ----------------------------------------
       * Default PLP behaviour
       * ---------------------------------------- */
      return this.productPriceProps?.selectedVariant || this.selectedVariant;
    },

    plpSelectedSizes() {
      const params = this.facetParams;
      if (!Array.isArray(params)) return [];

      return params
        .filter(([key]) => key === "filter.option_sizes")
        .map(([, value]) => value)
        .filter(Boolean);
    },

    plpSelectedColors() {
      const params = this.facetParams;
      if (!Array.isArray(params)) return [];

      return params
        .filter(([key]) => key === "filter.metafield_colors")
        .map(([, value]) => value)
        .filter(Boolean);
    },
  },

  created() {
    const saleTitle = this.metafields?.product_campaign_badge_title;
    const saleColor = this.metafields?.product_campaign_badge_color;

    const safeVariant =
      this.selectedVariant || this.product?.variants?.[0] || {};

    this.getSaleTag({
      tags: this.tags ?? [],
      campaign: { saleTitle, saleColor },
      price: safeVariant.price ?? 0,
      compareAtPrice: safeVariant.compareAtPrice ?? 0,
    });
  },

  methods: {
    ...mapActions("recommendations", ["trackBeacon"]),
    ...mapActions("productsSearch", ["trackSearchClickthrough"]),

    extractSize(v) {
      if (!v) return null;

      // Shopify selectedOptions
      if (v.selectedOptions) {
        if (
          v.selectedOptions.Size &&
          (v.selectedOptions.Size.value ?? v.selectedOptions.Size)
        ) {
          return v.selectedOptions.Size.value ?? v.selectedOptions.Size;
        }

        if (
          v.selectedOptions.size &&
          (v.selectedOptions.size.value ?? v.selectedOptions.size)
        ) {
          return v.selectedOptions.size.value ?? v.selectedOptions.size;
        }
      }

      // Searchspring selected_options array
      if (Array.isArray(v.selected_options)) {
        const found = v.selected_options.find(o => /size/i.test(o.name));
        return found?.value ?? null;
      }

      // Title-based fallback
      if (
        v.title &&
        /queen|king single|king|double|single|super king|long single/i.test(
          v.title
        )
      ) {
        const m = v.title.match(
          /(Single|Long Single|King Single|Double|Queen|King|Super King)/i
        );
        if (m) return m[0];
      }

      return null;
    },

    extractColor(v) {
      if (!v) return null;

      return (
        v.selectedOptions?.Colour?.value ||
        v.selectedOptions?.Color?.value ||
        v.selectedOptions?.colour ||
        v.selectedOptions?.color ||
        null
      );
    },

    getVariantImage(variant) {
      if (!variant) return null;

      if (variant.image) {
        return variant.image;
      }

      if (Array.isArray(variant.images) && variant.images.length) {
        return variant.images[0];
      }

      return null;
    },

    async handleClick(event) {
      const { source, id } = this.product;

      try {
        if (typeof window !== "undefined") {
          const scrollKey = `plp-scroll:${this.$route.fullPath}`;
          const data = {
            productId: this.product?.id,
            route: this.$route.fullPath,
          };
          sessionStorage.setItem(scrollKey, JSON.stringify(data));
        }

        if (source?.includes("searchspring-")) {
          const [, tag] = this.product.source.split("searchspring-");
          await this.trackBeacon({ type: "click", tag, productId: id });
        } else {
          // Search-module beacon clickthrough (PLP search results)
          await this.trackSearchClickthrough({ productId: id });
        }

        if (this.from !== "ShopifyProducts") {
          this.triggerGtmEvent();
        }

        if (event.target.closest("a")) {
          this.$searchspring.trackProductClick(
            event.target.closest("a"),
            this.product.intellisuggestData,
            this.product.intellisuggestSignature
          );
        }
      } catch (error) {
        this.$bugsnag.notify(error, e => {
          e.context = "ProductTile handleClick";
        });
      }
    },

    triggerGtmEvent() {
      this.$gtm.gtagEvent("select_item", {
        item_list_id: this.uid,
        item_list_name: this.uid,
        items: [
          {
            item_id: this.product?.id,
            item_sku: this.selectedVariant?.sku,
            item_name: this.product?.title,
            currency: "AUD",
            discount:
              this.selectedVariant?.compareAtPrice > this.selectedVariant?.price
                ? this.selectedVariant.compareAtPrice -
                  this.selectedVariant.price
                : 0,
            index: this.position >= 0 ? this.position + 1 : "",
            item_brand: this.product?.metafields?.brand_list ?? "",
            item_category: this.product?.metafields?.product_category ?? "",
            item_list_id: this.uid,
            item_list_name: this.uid,
            item_variant: this.selectedVariant?.selectedOptions?.size,
            price: this.selectedVariant?.price,
            quantity: 1,
          },
        ],
      });
    },
  },
};
